import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Brain, Activity, HeartHandshake, Users, Shield, Home, Accessibility } from 'lucide-react';
import SEO from '../components/SEO';

const VoorWie = () => {
  const lichamelijkeBeperking = {
    icon: Accessibility,
    title: 'Lichamelijke beperking',
    description: 'Begeleiding en ondersteuning voor jongeren met een lichamelijke beperking, met aandacht voor persoonlijke verzorging en mobiliteit.',
    features: [
      'Persoonlijke verzorging en ADL-ondersteuning',
      'Aangepaste activiteiten',
      'Hulp bij mobiliteit',
      'Medische begeleiding waar nodig',
      'Veilige en toegankelijke omgeving'
    ],
    color: '#9B59B6',
    gradient: 'from-[#F3E5F5] to-white'
  };

  const verstandelijkeBeperking = [
    {
      icon: Brain,
      title: 'ASS (Autisme Spectrum Stoornis)',
      description: 'Gespecialiseerde begeleiding voor kinderen en jongeren met autisme, met focus op structuur, voorspelbaarheid en duidelijke communicatie.',
      features: [
        'Visuele ondersteuning en pictogrammen',
        'Duidelijke dagstructuur en routines',
        'Prikkelarme ruimtes',
        'Begrijpelijke en concrete instructies',
        'Voorspelbare omgeving'
      ],
      color: '#5DADE2',
      gradient: 'from-[#E0F2F7] to-white'
    },
    {
      icon: Activity,
      title: 'ADHD',
      description: 'Intensieve 1-op-1 begeleiding voor jongeren met ADHD, gericht op rust, focus, emotieregulatie en het ontwikkelen van zelfcontrole.',
      features: [
        'Korte, afgebakende activiteiten',
        'Regelmatige bewegingsmomenten',
        'Duidelijke grenzen en regels',
        'Positieve bekrachtiging',
        'Hulp bij emotieregulatie'
      ],
      color: '#F39C12',
      gradient: 'from-[#FFF4E0] to-white'
    },
    {
      icon: HeartHandshake,
      title: 'Gedragsproblematiek',
      description: 'Deskundige begeleiding bij gedragsproblemen met expertise in moeilijk verstaanbaar gedrag (MVG), met focus op positieve benadering.',
      features: [
        'Gedragsanalyse en begeleiding',
        'Positieve gedragsondersteuning',
        'Duidelijke afspraken en consequenties',
        'Begeleiding bij frustraties',
        'Samenwerking met ouders/verzorgers'
      ],
      color: '#52BE80',
      gradient: 'from-[#E8F5E9] to-white'
    }
  ];

  const renderGroupCard = (group, index, isReversed = false) => {
    const Icon = group.icon;
    return (
      <Card className={`overflow-hidden rounded-3xl border-none shadow-xl bg-gradient-to-br ${group.gradient}`}>
        <div className="grid md:grid-cols-2 gap-8 items-center p-8 md:p-12">
          <div className={isReversed ? 'order-1 md:order-2' : 'order-1'}>
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: group.color }}
            >
              <Icon className="w-10 h-10 text-white" />
            </div>
            <h3 
              className="text-3xl md:text-4xl font-bold text-[#1A4D4D] mb-4"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              {group.title}
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              {group.description}
            </p>
            <h4 className="text-xl font-bold text-[#1A4D4D] mb-3">Onze aanpak:</h4>
            <ul className="space-y-2">
              {group.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3 text-gray-600">
                  <span 
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: group.color }}
                  ></span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div className={isReversed ? 'order-2 md:order-1' : 'order-2'}>
            <div className="bg-white/50 rounded-2xl p-8 text-center">
              <div className="mb-4">
                <span className="text-sm text-gray-500 uppercase tracking-wide">Leeftijd</span>
                <p className="text-2xl font-bold text-[#1A4D4D]">3-18 jaar</p>
              </div>
              <div>
                <span className="text-sm text-gray-500 uppercase tracking-wide">Beschikbaar</span>
                <p className="text-xl font-bold" style={{ color: group.color }}>Dagopvang & Logeren</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Voor Wie - Doelgroep 3-18 jaar"
        description="Zorg voor jeugd 3-18 jaar met ASS, ADHD, lichamelijke of verstandelijke beperking. 1-op-1 begeleiding en groepsverband. Bel 06-19199023."
        keywords="voor wie, doelgroep, ASS, ADHD, autisme, gedragsproblematiek, lichamelijke beperking, verstandelijke beperking"
        
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-6xl font-bold text-[#1A4D4D] mb-6"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Voor Wie
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Onze zorg is er voor jeugd van 3 tot 18 jaar met een lichamelijke of verstandelijke beperking, waaronder ASS (Autisme Spectrum Stoornis), ADHD, gedragsproblematiek of een behoefte aan structuur, rust en voorspelbaarheid. We bieden intensieve 1-op-1 begeleiding in een kleinschalige, huiselijke omgeving. Daarnaast bieden we dagbesteding in een klein groepsverband van maximaal 3 jeugdigen, waarbij persoonlijke aandacht, structuur en een veilige omgeving centraal staan.
            </p>
          </div>
        </div>
      </section>

      {/* Lichamelijke beperking */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#1A4D4D] mb-8 text-center"
            style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
          >
            Lichamelijke beperking
          </h2>
          <div className="mb-16">
            {renderGroupCard(lichamelijkeBeperking, 0, false)}
          </div>
        </div>
      </section>

      {/* Verstandelijke beperking */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#1A4D4D] mb-8 text-center"
            style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
          >
            Verstandelijke beperking
          </h2>
          <div className="space-y-12">
            {verstandelijkeBeperking.map((group, index) => (
              <div key={index}>
                {renderGroupCard(group, index, index % 2 !== 0)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A4D4D] mb-8 text-center"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Leeftijd & Voorwaarden
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="p-6 rounded-3xl border-none shadow-lg text-center bg-gray-50">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E0F2F7] to-[#B3E5FC] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-[#5DADE2]" />
                </div>
                <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Leeftijd</h3>
                <p className="text-gray-600">3 tot 18 jaar</p>
              </Card>

              <Card className="p-6 rounded-3xl border-none shadow-lg text-center bg-gray-50">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FFE8E0] to-[#FFD0C0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-[#FF6347]" />
                </div>
                <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Veiligheid</h3>
                <p className="text-gray-600">24/7 Professionele zorg</p>
              </Card>

              <Card className="p-6 rounded-3xl border-none shadow-lg text-center bg-gray-50">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Home className="w-8 h-8 text-[#52BE80]" />
                </div>
                <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Setting</h3>
                <p className="text-gray-600">Kleinschalig & Huiselijk</p>
              </Card>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-[#1A4D4D] mb-4 text-center">Belangrijk om te weten</h3>
              <div className="space-y-4 text-gray-600">
                <p className="leading-relaxed">
                  <strong>Intensieve begeleiding:</strong> Wij werken met 1-op-1 begeleiding, wat betekent dat elk kind de volledige aandacht krijgt die het nodig heeft. Dit zorgt voor maximale veiligheid en persoonlijke ontwikkeling.
                </p>
                <p className="leading-relaxed">
                  <strong>Indicatie nodig:</strong> Voor onze zorg is een indicatie vereist (WLZ, WMO of Jeugdwet). Tijdens de intake bespreken we samen wat het beste past bij uw situatie.
                </p>
                <p className="leading-relaxed">
                  <strong>Kennismakingsgesprek:</strong> Voor aanmelding nodigen we u en uw kind uit voor een vrijblijvend kennismakingsgesprek en rondleiding in onze aanleunwoning.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <h3 className="text-2xl font-bold text-[#1A4D4D] mb-4">Is onze zorg geschikt voor uw kind?</h3>
              <p className="text-gray-600 mb-6">Neem contact op voor een vrijblijvend gesprek.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:info@kscare.nl?subject=Vraag%20over%20geschiktheid%20zorg">
                  <Button 
                    className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg"
                  >
                    Neem contact op
                  </Button>
                </a>
                <a href="tel:0619199023">
                  <Button 
                    variant="outline"
                    className="border-2 border-[#1A4D4D] text-[#1A4D4D] hover:bg-[#1A4D4D] hover:text-white rounded-full px-8 py-6 text-lg"
                  >
                    Bel 06-19199023
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VoorWie;
